import React, { useEffect } from 'react';
import { Platform } from 'react-native';
import * as Notifications from 'expo-notifications';
import { useCalendarReminderStore } from '../store/calendarReminderStore';
import { CalendarReminderAlertScreen } from '../screens/CalendarReminderAlertScreen';

/**
 * Mounted once at the App.tsx root, sibling to AppNavigator — the only way
 * to get a TRUE full-screen takeover independent of currentScreen (see
 * calendarReminderStore.ts's doc comment: AppNavigator's own AppContext is
 * re-instantiated per switch-case, unreachable from outside its tree, so
 * this drives navigation back through the pendingNavigation bridge instead).
 *
 * Listens for calendar_reminder notifications from BOTH triggers described
 * in the reminder pipeline: (a) tapping a local or remote notification
 * (addNotificationResponseReceivedListener — covers cold-start, background,
 * and foreground taps alike) and (b) a notification being RECEIVED while
 * foregrounded (addNotificationReceivedListener) — shown immediately rather
 * than waiting for a tap, since a foregrounded user has no OS banner to tap.
 * fcm.service.ts's own tap handling is remote-FCM-specific and routes
 * through its separate clickAction/navigationHandler system; this listens
 * independently since local notifications (calendar-local-reminders.service.ts)
 * never go through that FCM-only path at all.
 */
export const CalendarReminderAlertRoot: React.FC = () => {
  const activeReminder = useCalendarReminderStore((state) => state.activeReminder);
  const showReminder = useCalendarReminderStore((state) => state.showReminder);

  useEffect(() => {
    if (Platform.OS === 'web') return;

    const toReminder = (data: any) => {
      if (!data || data.type !== 'calendar_reminder' || !data.calendarEventId) return null;
      return {
        calendarEventId: String(data.calendarEventId),
        title: data.title || 'Upcoming event',
        startDate: data.startDate || new Date().toISOString(),
        location: data.location || undefined,
        minutesBefore: data.minutesBefore != null ? Number(data.minutesBefore) : undefined,
      };
    };

    const receivedSub = Notifications.addNotificationReceivedListener((notification) => {
      const reminder = toReminder(notification.request.content.data);
      if (reminder) showReminder(reminder);
    });

    const responseSub = Notifications.addNotificationResponseReceivedListener((response) => {
      const reminder = toReminder(response.notification.request.content.data);
      if (reminder) showReminder(reminder);
    });

    return () => {
      receivedSub.remove();
      responseSub.remove();
    };
  }, [showReminder]);

  if (!activeReminder) return null;
  return <CalendarReminderAlertScreen reminder={activeReminder} />;
};
